import { type QueryClient, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";
import { useAnalytics } from "../contexts/analytics/hooks";
import { backendService } from "../services/backend.service";
import type { JobNotificationChange, JobNotificationState, NurseJobNotificationsResponse } from "../utils/jobNotificationTypes";
import { DEPARTMENT_SILENT_JOBS_QUERY_KEY } from "./useDepartmentSilentJobs";
import { useGetResolverJobDisplayName } from "./useJobDisplayName";

/** Per-job "New open shift" settings for one employee. */
export const NURSE_JOB_NOTIFICATIONS_QUERY_KEY = "nurse-job-notifications";

export const nurseJobNotificationsQueryKey = (departmentId: string, nurseId: number) => [NURSE_JOB_NOTIFICATIONS_QUERY_KEY, departmentId, nurseId] as const;

/** Changing an employee's jobs can add silent jobs, so refetch the profile and the Team markers. */
export const invalidateJobNotificationQueries = (queryClient: QueryClient, departmentId: string, nurseId: number): Promise<void> =>
	Promise.all([
		queryClient.invalidateQueries({ queryKey: nurseJobNotificationsQueryKey(departmentId, nurseId) }),
		queryClient.invalidateQueries({ queryKey: [DEPARTMENT_SILENT_JOBS_QUERY_KEY, departmentId] }),
	]).then(() => undefined);

/**
 * For writes that change many employees' jobs at once, like a bulk job change or a mapping save.
 * New jobs start silent, so every cached read may be stale.
 */
export const invalidateAllJobNotificationQueries = (queryClient: QueryClient): Promise<void> =>
	Promise.all([
		queryClient.invalidateQueries({ queryKey: [NURSE_JOB_NOTIFICATIONS_QUERY_KEY] }),
		queryClient.invalidateQueries({ queryKey: [DEPARTMENT_SILENT_JOBS_QUERY_KEY] }),
	]).then(() => undefined);

/** Reads one employee's job notifications, with a lookup by job id. Off while `nurseId` is undefined (unsaved profile). */
export const useNurseJobNotificationsQuery = (departmentId: string, nurseId: number | undefined) => {
	const query = useQuery({
		// Matches `nurseJobNotificationsQueryKey`, so the save's cache write lands here.
		queryKey: [NURSE_JOB_NOTIFICATIONS_QUERY_KEY, departmentId, nurseId],
		queryFn: async () => {
			if (nurseId === undefined) throw new Error("useNurseJobNotificationsQuery ran without a nurse id");
			return (await backendService.getNurseJobNotifications(departmentId, nurseId)).data;
		},
		enabled: nurseId !== undefined && nurseId > 0,
	});

	// DEFAULT: an empty lookup before data arrives. Callers treat a missing job as "unknown", not "notified".
	const stateByJobId = useMemo(() => new Map<string, JobNotificationState>((query.data?.jobs ?? []).map((job) => [job.jobId, job])), [query.data]);
	const silentByJobId = useMemo(() => new Map<string, boolean>([...stateByJobId].map(([jobId, state]) => [jobId, state.isSilent])), [stateByJobId]);

	return { ...query, stateByJobId, silentByJobId };
};

/** Where in the profile a job notification save came from. */
export type JobNotificationSaveSource = "profile_chip" | "profile_undo" | "profile_edit";

type SaveVariables = { nurseId: number; source: JobNotificationSaveSource; changes: JobNotificationChange[] };

/**
 * Saves only the jobs sent, then refreshes the Team markers and tracks a mute or unmute per saved job.
 * Callers show their own success and error messages because each words them differently.
 */
export const useSaveNurseJobNotifications = (departmentId: string) => {
	const queryClient = useQueryClient();
	const { track } = useAnalytics();
	const jobDisplayName = useGetResolverJobDisplayName();
	return useMutation({
		mutationFn: async ({ nurseId, changes }: SaveVariables): Promise<NurseJobNotificationsResponse> =>
			(await backendService.updateNurseJobNotifications(departmentId, nurseId, { changes })).data,
		onSuccess: (data, { nurseId, source, changes }) => {
			queryClient.setQueryData(nurseJobNotificationsQueryKey(departmentId, nurseId), data);
			queryClient.invalidateQueries({ queryKey: [DEPARTMENT_SILENT_JOBS_QUERY_KEY, departmentId] });
			// One place for every admin caller, fired only after the server stored the change.
			for (const change of changes) {
				track(change.isSilent ? "job_notification_muted" : "job_notification_unmuted", {
					source,
					nurse_id: nurseId,
					job_id: change.jobId,
					job_name: jobDisplayName(change.jobId),
				});
			}
		},
	});
};
